import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import LiveTripMap from '../../components/LiveTripMap';
import {
  Badge, Button, Chip, Empty, Input, Loading, Modal, Row,
} from '../../components/Ui';
import { Reveal, Pulse } from '../../components/Motion';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import {
  connectRealtime, onRealtime, goOnline, goOffline, publishLocation, watchTrip,
} from '../../services/realtime';
import {
  requestLocationPermission, getCurrentPosition, watchPosition,
} from '../../services/location';
import { colors } from '../../theme';
import { currency, titleCase } from '../../utils/format';
import { courierOnly } from '../../utils/partnerTypes';

/** A rider on either of these carries parcels - 'courier' is what the courier sign-up stores. */
const PARCEL_VEHICLES = ['bike', 'courier'];

const OFFER_SECONDS = 20;

/** Full-screen incoming request. Counts down and auto-declines, like a ride app. */
const OfferDialog = ({ offer, onAccept, onReject, busy }) => {
  const [left, setLeft] = useState(OFFER_SECONDS);

  useEffect(() => {
    if (!offer) return undefined;
    setLeft(OFFER_SECONDS);

    // Buzz the device so a driver notices without staring at the screen.
    if (navigator.vibrate) navigator.vibrate([400, 200, 400]);

    const t = setInterval(() => setLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [offer]);

  useEffect(() => {
    if (offer && left === 0) onReject(true);
  }, [left, offer, onReject]);

  if (!offer) return null;
  const isCourier = offer.serviceType === 'courier';
  const isDelivery = offer.serviceType === 'delivery';

  return (
    <Modal open onClose={() => onReject(false)}>
      <div
        style={{
          height: 4, background: colors.line, borderRadius: 2,
          overflow: 'hidden', marginBottom: 16,
        }}
      >
        <div
          style={{
            height: 4, background: colors.accent, borderRadius: 2,
            width: `${(left / OFFER_SECONDS) * 100}%`, transition: 'width 1s linear',
          }}
        />
      </div>

      <div className="row" style={{ gap: 12 }}>
        <Pulse><span style={{ fontSize: 30 }}>{isDelivery ? '🛵' : isCourier ? '📦' : '🚕'}</span></Pulse>
        <div className="grow">
          <h3 className="h3">{isDelivery ? 'New order to deliver' : isCourier ? 'New delivery' : 'New ride request'}</h3>
          <div className="t-tiny">
            {isDelivery ? `Order ${offer.orderNo} · ${offer.shopName}` : `${titleCase(offer.vehicleType)} · ${offer.rideNo}`}
          </div>
        </div>
        <span style={{ fontSize: 22, fontWeight: 900, color: colors.accent }}>{left}s</span>
      </div>

      <div
        style={{
          background: colors.successBg, borderRadius: 14, padding: 14,
          textAlign: 'center', marginTop: 16,
        }}
      >
        <div style={{ fontSize: 28, fontWeight: 900, color: colors.success }}>
          {currency(offer.fare)}
        </div>
        <div className="t-tiny">
          {offer.distanceKm} km trip · {offer.etaToPickupMin} min to pickup
        </div>
      </div>

      <div className="row" style={{ gap: 14, marginTop: 16, alignItems: 'stretch' }}>
        <div className="col" style={{ alignItems: 'center', paddingTop: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 5, background: colors.success }} />
          <span className="grow" style={{ width: 2, background: colors.line, margin: '4px 0' }} />
          <span style={{ width: 10, height: 10, borderRadius: 5, background: colors.danger }} />
        </div>
        <div className="grow col" style={{ gap: 12 }}>
          <div>
            <div className="t-tiny">PICKUP</div>
            <div className="t-body clamp-2">{(offer.pickup && offer.pickup.address) || 'Nearby'}</div>
          </div>
          <div>
            <div className="t-tiny">DROP</div>
            <div className="t-body clamp-2">{(offer.drop && offer.drop.address) || '—'}</div>
          </div>
        </div>
      </div>

      {isDelivery ? (
        <div style={{ background: colors.bg, padding: 12, borderRadius: 12, marginTop: 12 }}>
          <span className="t-small">
            🧾 {offer.itemCount} item{offer.itemCount > 1 ? 's' : ''} · you earn {currency(offer.fare)} (₹{offer.perKmRate}/km)
            {offer.codAmount ? ` · collect ${currency(offer.codAmount)} cash on delivery` : ' · already paid online'}
          </span>
        </div>
      ) : null}

      {isCourier && offer.parcel ? (
        <div style={{ background: colors.bg, padding: 12, borderRadius: 12, marginTop: 12 }}>
          <span className="t-small">
            📦 {titleCase(offer.parcel.type || 'Parcel')}
            {offer.parcel.weightKg ? ` · ${offer.parcel.weightKg} kg` : ''}
            {offer.parcel.codAmount ? ` · collect ${currency(offer.parcel.codAmount)}` : ''}
          </span>
        </div>
      ) : null}

      <div className="row" style={{ gap: 12, marginTop: 20 }}>
        <Button title="Decline" variant="light" full onClick={() => onReject(false)} />
        <Button title="Accept" icon="✓" loading={busy} full onClick={onAccept} />
      </div>
    </Modal>
  );
};

/** Driver home: duty toggle, incoming offers, and the trip in progress. */
/** Metres between two [lng, lat] points. */
const metresBetween = (a, b) => {
  if (!a || !b) return null;
  const R = 6371000; const d = (x) => (x * Math.PI) / 180;
  const dLat = d(b[1] - a[1]); const dLng = d(b[0] - a[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(d(a[1])) * Math.cos(d(b[1])) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(h)));
};
const fmtM = (m) => (m == null ? '' : m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`);

const CANCEL_REASONS = [
  'Vehicle breakdown', 'Shop is closed', 'Order not ready after long wait',
  'Customer not reachable', 'Wrong address', 'Personal emergency',
];

const DriverRidesPage = () => {
  const { partner } = useAuth();
  const { config } = useApp();
  const toast = useToast();
  const vehicleType = (partner && partner.vehicleDetails && partner.vehicleDetails.type) || 'cab';

  const [online, setOnline] = useState(Boolean(partner && partner.isOpen));
  const [connected, setConnected] = useState(false);
  const [offer, setOffer] = useState(null);
  // Short banner explaining why an offer vanished on its own.
  const [takenNote, setTakenNote] = useState(null);
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [otp, setOtp] = useState('');
  const [myPos, setMyPos] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [collectOpen, setCollectOpen] = useState(false);
  const [qr, setQr] = useState(null);
  const [qrState, setQrState] = useState('idle');   // idle | loading | waiting | paid | error
  const [cash, setCash] = useState(null);

  const loadCash = useCallback(() => api.partners.myCash().then((r) => setCash(r.data)).catch(() => {}), []);
  useEffect(() => { loadCash(); }, [loadCash]);
  const stopWatch = useRef(null);

  const loadActive = useCallback(async () => {
    try {
      const res = await api.rides.active();
      setTrip(res.data || null);
      if (res.data) watchTrip(res.data._id);
    } catch (e) { /* offline */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadActive(); }, [loadActive]);

  /**
   * Without a socket (e.g. the API is on serverless) offers would never arrive,
   * so poll for them while on duty and disconnected.
   */
  useEffect(() => {
    if (!online || connected) return undefined;
    const timer = setInterval(async () => {
      try {
        const res = await api.rides.offers();
        const open = res.data || [];
        const next = open[0];
        // Reconcile both ways: an offer that has left the open list was taken
        // or cancelled, so it must leave the screen too.
        setOffer((cur) => {
          if (!cur || open.some((r) => String(r._id) === cur.rideId)) return cur;
          setTakenNote('Taken by another driver');
          return null;
        });
        if (next) {
          setOffer((cur) => cur || {
            rideId: String(next._id),
            rideNo: next.rideNo,
            serviceType: next.serviceType,
            vehicleType: next.vehicleType,
            pickup: next.pickup,
            drop: next.drop,
            parcel: next.parcel,
            fare: next.estimate && next.estimate.total,
            distanceKm: next.estimate && next.estimate.distanceKm,
            etaToPickupMin: next.etaToPickupMin,
          });
        }
      } catch (err) { /* keep polling */ }
    }, 6000);
    return () => clearInterval(timer);
  }, [online, connected]);

  // Socket: offers arrive here the moment dispatch makes them.
  useEffect(() => {
    connectRealtime().then(() => setConnected(true));
    const offs = [
      onRealtime('connection', (s) => setConnected(s.connected)),
      onRealtime('ride:offer', (o) => setOffer((cur) => cur || o)),
      // Another driver got there first - take it off this screen immediately
      // rather than leaving a request they can no longer win.
      onRealtime('ride:taken', ({ rideId, cancelled }) => {
        setOffer((cur) => {
          if (!cur || cur.rideId !== rideId) return cur;
          setTakenNote(cancelled ? 'Request cancelled by the customer' : 'Taken by another driver');
          return null;
        });
      }),
      onRealtime('ride:cancelled', () => {
        setOffer(null);
        setTrip(null);
        toast.error('The customer cancelled this trip.', 'Trip cancelled');
      }),
    ];
    return () => offs.forEach((fn) => fn());
  }, [toast]);

  /** While on duty, stream position so customers see the vehicle move. */
  const tripId = trip && trip._id;
  useEffect(() => {
    // A rider carrying a trip keeps streaming even with duty toggled off -
    // "Reached the shop" needs their position.
    if (!online && !tripId) {
      if (stopWatch.current) stopWatch.current();
      stopWatch.current = null;
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const ok = await requestLocationPermission();
      if (!ok || cancelled) return;
      const fix = await getCurrentPosition();
      if (fix) {
        goOnline(fix.coordinates, vehicleType);
        // Tell dispatch where we actually are the moment the page opens.
        api.rides.heartbeat({ coordinates: fix.coordinates }).catch(() => {});
      }

      // Idle heartbeat - dispatch queries the stored position, so an on-duty
      // rider with no trip must keep it fresh or they are invisible.
      let lastBeat = 0;
      stopWatch.current = watchPosition((pos) => {
        setMyPos(pos.coordinates);
        publishLocation({ ...pos, rideId: tripId });
        if (tripId) {
          api.rides.pushLocation(tripId, pos).catch(() => {});
        } else if (Date.now() - lastBeat > 30000) {
          lastBeat = Date.now();
          api.rides.heartbeat({ coordinates: pos.coordinates }).catch(() => {});
        }
      });
    })();
    return () => { cancelled = true; if (stopWatch.current) stopWatch.current(); };
  }, [online, vehicleType, tripId]);

  // While a QR is displayed, ask the server whether Razorpay has seen payment.
  useEffect(() => {
    if (qrState !== 'waiting' || !qr) return undefined;
    const t = setInterval(async () => {
      try {
        const res = await api.payments.doorStatus(qr.paymentId);
        if (res.data.status === 'paid') { clearInterval(t); setQrState('paid'); finishDelivery('online'); }
        if (res.data.status === 'failed') { clearInterval(t); setQrState('error'); }
      } catch (e) { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrState, qr]);

  const toggleDuty = async (value) => {
    setOnline(value);
    try {
      const fix = value ? await getCurrentPosition() : null;
      await api.rides.availability({ online: value, coordinates: fix && fix.coordinates });
      if (value) goOnline(fix && fix.coordinates, vehicleType); else goOffline();
    } catch (err) {
      setOnline(!value);
      toast.error(err.message, 'Could not update');
    }
  };

  const acceptOffer = async () => {
    setBusy(true);
    try {
      const res = await api.rides.accept(offer.rideId);
      setOffer(null);
      setTrip(res.data);
      watchTrip(res.data._id);
    } catch (err) {
      setOffer(null);
      toast.error(err.message, 'Too late');
    } finally { setBusy(false); }
  };

  const rejectOffer = useCallback((timedOut) => {
    setOffer((cur) => {
      if (cur && cur.rideId && !timedOut) api.rides.reject(cur.rideId).catch(() => {});
      return null;
    });
  }, []);

  /**
   * A fresh fix for the two buttons the server fences. The watcher's last
   * point can be stale; asking again at the click is what makes the button
   * work the moment the rider pulls up. Falls back to the last known point -
   * the server then answers with the distance instead of the button doing
   * nothing.
   */
  const freshPosition = async () => {
    const fix = await getCurrentPosition({ fresh: true });
    if (fix) { setMyPos(fix.coordinates); return fix.coordinates; }
    return myPos;
  };

  const step = async (fn, label) => {
    setBusy(true);
    try {
      const res = await fn();
      setTrip(res.data);
      if (label) toast.success(label);
    } catch (err) {
      toast.error(err.message, 'Could not update');
    } finally { setBusy(false); }
  };

  const completeTrip = async () => {
    const ok = await toast.confirm({
      title: 'End trip',
      message: 'Collect the fare and close this trip?',
      confirmLabel: 'End trip',
      cancelLabel: 'Not yet',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await api.rides.complete(trip._id, {});
      const f = res.data.final;
      setTrip(null);
      toast.success(
        `Collect ${currency(f.total)} — ${f.distanceKm} km · ${f.durationMin} min · ${f.waitingMin} min waiting`,
        'Trip completed',
      );
    } catch (err) {
      toast.error(err.message, 'Could not complete');
    } finally { setBusy(false); }
  };

  /**
   * Distance to the next stop, against the admin fence. The server checks the
   * same number - this only stops the rider tapping a button that would fail.
   */
  const isDelivery = trip && trip.serviceType === 'delivery';
  const fenceM = (config && config.delivery && config.delivery.geofenceM) || 500;
  const nextStop = trip && (trip.status === 'ongoing' ? trip.drop : trip.pickup);
  const distToStop = isDelivery && nextStop ? metresBetween(myPos, nextStop.coordinates) : null;
  const inFence = !isDelivery || (distToStop != null && distToStop <= fenceM);

  /** Finish a delivery, saying how the money was taken. */
  const finishDelivery = async (method) => {
    setBusy(true);
    try {
      const res = await api.rides.complete(trip._id, { method, coordinates: await freshPosition() });
      const f = res.data.final;
      setCollectOpen(false); setQr(null); setQrState('idle');
      setTrip(null); loadCash();
      toast.success(`You earned ${currency(f.total)} for this ${f.distanceKm} km delivery.`, 'Delivered');
    } catch (err) { toast.error(err.message, 'Could not complete'); } finally { setBusy(false); }
  };

  /** Ask the server for a Razorpay QR the customer can scan at the door. */
  const showDoorQr = async () => {
    setQrState('loading');
    try {
      const res = await api.payments.doorQr(trip.order);
      setQr(res.data); setQrState('waiting');
    } catch (err) { setQrState('error'); toast.error(err.message); }
  };

  const cancelTrip = async () => {
    // Deliveries need a reason, and after pickup it goes to admin - so the
    // plain confirm is only right for passenger trips.
    if (isDelivery) { setCancelOpen(true); return; }
    const ok = await toast.confirm({
      title: 'Cancel trip',
      message: 'Cancelling too often affects your rating.',
      confirmLabel: 'Cancel trip',
      cancelLabel: 'Keep trip',
      danger: true,
    });
    if (!ok) return;
    await step(() => api.rides.driverCancel(trip._id, { reason: 'Cancelled by driver' }));
    setTrip(null);
  };

  const submitCancel = async () => {
    setBusy(true);
    try {
      const res = await api.rides.driverCancel(trip._id, { reason: cancelReason });
      setCancelOpen(false); setCancelReason('');
      if (res.data && res.data.status === 'cancelled') setTrip(null); else setTrip(res.data);
      toast.success(res.message);
    } catch (err) { toast.error(err.message, 'Could not cancel'); } finally { setBusy(false); }
  };

  if (loading) return <Loading text="Checking for trips…" />;

  const isCourier = courierOnly(partner);

  return (
    <div style={{ maxWidth: 720 }}>
      <PageHeader
        title={isCourier ? 'Deliveries' : 'Rides'}
        back={false}
        subtitle={PARCEL_VEHICLES.includes(vehicleType) && config && config.delivery
          ? `Bike · deliveries pay ₹${config.delivery.perKmRate}/km (min ₹${config.delivery.minPayout}) · ${connected ? 'connected' : 'reconnecting…'}`
          : `${titleCase(vehicleType)} · ${connected ? 'connected' : 'reconnecting…'}`}
        right={(
          <span
            style={{
              padding: '4px 10px', borderRadius: 8, fontSize: 10, fontWeight: 800,
              background: connected ? colors.successBg : colors.dangerBg,
              color: connected ? colors.success : colors.danger,
            }}
          >
            {connected ? 'LIVE' : 'OFF'}
          </span>
        )}
      />

      <div
        className="card sh-sm row-between"
        style={{
          gap: 14, background: online ? colors.successBg : '#fff',
          borderColor: online ? '#BBEBCD' : colors.line,
        }}
      >
        <div className="grow">
          <h3 className="h3" style={{ color: online ? colors.success : colors.ink }}>
            {online ? "You're on duty" : "You're off duty"}
          </h3>
          <div className="t-small">
            {online
              ? 'Requests near you will ring on this page. Keep it open.'
              : 'Go on duty to start receiving requests.'}
          </div>
        </div>
        <button
          type="button"
          onClick={() => toggleDuty(!online)}
          aria-label={online ? 'Go off duty' : 'Go on duty'}
          style={{
            width: 52, height: 30, borderRadius: 15, border: 0, flex: '0 0 auto',
            background: online ? colors.success : colors.line,
            position: 'relative', transition: 'background .2s ease',
          }}
        >
          <span
            style={{
              position: 'absolute', top: 3, left: online ? 25 : 3,
              width: 24, height: 24, borderRadius: 12, background: '#fff',
              transition: 'left .2s cubic-bezier(.22,1,.36,1)',
              boxShadow: '0 1px 4px rgba(0,0,0,.2)',
            }}
          />
        </button>
      </div>

      {takenNote ? (
        <Reveal
          style={{
            background: colors.warningBg, color: colors.warning, padding: 12,
            borderRadius: 12, marginTop: 12, fontSize: 12.5, fontWeight: 700,
          }}
        >
          ⏱ {takenNote}
        </Reveal>
      ) : null}

      {trip ? (
        <Reveal className="card sh-md" style={{ marginTop: 16 }}>
          <div className="row-between">
            <h3 className="h3">
              {trip.serviceType === 'courier' ? 'Delivery' : 'Trip'} {trip.rideNo}
            </h3>
            <Badge value={trip.status} />
          </div>

          <div className="row" style={{ gap: 14, marginTop: 14, alignItems: 'stretch' }}>
            <div className="col" style={{ alignItems: 'center', paddingTop: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: 5, background: colors.success }} />
              <span className="grow" style={{ width: 2, background: colors.line, margin: '4px 0' }} />
              <span style={{ width: 10, height: 10, borderRadius: 5, background: colors.danger }} />
            </div>
            <div className="grow col" style={{ gap: 12 }}>
              <div>
                <div className="t-tiny">PICKUP</div>
                <div className="t-body">{(trip.pickup && trip.pickup.address) || '—'}</div>
              </div>
              <div>
                <div className="t-tiny">DROP</div>
                <div className="t-body">{(trip.drop && trip.drop.address) || '—'}</div>
              </div>
            </div>
          </div>

          <div
            className="row"
            style={{ gap: 10, background: colors.bg, padding: 12, borderRadius: 12, marginTop: 14 }}
          >
            <span style={{ fontSize: 16 }}>👤</span>
            <div className="grow">
              <div className="t-title">{(trip.user && trip.user.name) || 'Customer'}</div>
              <div className="t-tiny">
                {currency(trip.estimate && trip.estimate.total)} est ·{' '}
                {trip.estimate && trip.estimate.distanceKm} km
              </div>
            </div>
            <a
              href={`tel:${trip.user && trip.user.phone}`}
              className="press"
              style={{
                width: 34, height: 34, borderRadius: 10, background: '#fff',
                display: 'grid', placeItems: 'center', flex: '0 0 auto',
              }}
              aria-label="Call customer"
            >
              📞
            </a>
          </div>

          {trip.serviceType === 'delivery' && trip.parcel && trip.parcel.description ? (
            <div style={{
              background: colors.primaryLight, color: colors.primary, borderRadius: 12,
              padding: '8px 12px', marginTop: 12, fontWeight: 700,
            }}>
              {trip.parcel.description}
            </div>
          ) : null}

          {/* Distance is information; the server enforces the fence when the button is clicked. */}
          {isDelivery && trip.status !== 'ongoing' && !inFence ? (
            <div style={{ background: colors.bg, borderRadius: 12, padding: 12, marginTop: 12 }} className="t-small">
              📍 {distToStop == null ? 'Waiting for your location…' : `${fmtM(distToStop)} from the shop`}
              {' '}· confirm within {fmtM(fenceM)} of the shop
              {' '}<button type="button" className="link-btn" onClick={() => freshPosition()}>↻ Refresh my location</button>
            </div>
          ) : null}

          {isDelivery && trip.status === 'ongoing' ? (
            <div style={{
              borderRadius: 12, padding: 12, marginTop: 12,
              background: trip.parcel && trip.parcel.codAmount ? 'var(--warning-bg)' : 'var(--success-bg)',
            }} className="t-small">
              <strong style={{ color: trip.parcel && trip.parcel.codAmount ? 'var(--warning)' : 'var(--success)' }}>
                {trip.parcel && trip.parcel.codAmount
                  ? `💵 Collect ${currency(trip.parcel.codAmount)} · cash or QR`
                  : '✅ Paid online · nothing to collect'}
              </strong>
              {!inFence ? (
                <div className="t-tiny" style={{ marginTop: 3 }}>
                  {distToStop == null ? 'Waiting for your location…' : `${fmtM(distToStop)} from the customer`}
                  {' '}· confirm within {fmtM(fenceM)} of the customer
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Own GPS feeds the bike directly - no round trip through the server. */}
          {trip.pickup && trip.drop ? (
            <LiveTripMap ride={trip} who="rider" selfPosition={myPos} height={220} style={{ marginTop: 12 }} />
          ) : null}

          {trip.cancelRequest && trip.cancelRequest.status === 'pending' ? (
            <div style={{ background: 'var(--warning-bg)', borderRadius: 12, padding: 12, marginTop: 12 }} className="t-small">
              ⏳ Cancellation sent to admin. Please continue the delivery until they decide.
            </div>
          ) : null}

          {trip.status === 'accepted' ? (
            <Button
              title={trip.serviceType === 'delivery' ? 'Reached the shop' : "I've arrived at pickup"}
              icon="📍"
              loading={busy}
              full
              style={{ marginTop: 14 }}
              onClick={() => step(async () => api.rides.arrived(trip._id, { coordinates: await freshPosition() }),
                trip.serviceType === 'delivery' ? 'Collect the order and confirm pickup' : 'Waiting time started')}
            />
          ) : null}

          {/* A delivery has no OTP - the shop hands the parcel over. */}
          {trip.status === 'arrived' && trip.serviceType === 'delivery' ? (
            <Button
              title="Picked up - start delivery"
              icon="🛵"
              loading={busy}
              full
              style={{ marginTop: 14 }}
              onClick={() => step(() => api.rides.start(trip._id, {}), 'On your way - customer notified')}
            />
          ) : null}

          {trip.status === 'arrived' && trip.serviceType !== 'delivery' ? (
            <div style={{ marginTop: 14 }}>
              <div className="t-small" style={{ marginBottom: 8 }}>
                Ask the customer for their 4-digit OTP to start.
              </div>
              <Input
                placeholder="0000"
                inputMode="numeric"
                maxLength={4}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                style={{ marginBottom: 10 }}
                inputStyle={{ textAlign: 'center' }}
              />
              <Button
                title="Start trip"
                loading={busy}
                disabled={otp.length !== 4}
                full
                onClick={() => step(() => api.rides.start(trip._id, { otp }).then((r) => { setOtp(''); return r; }))}
              />
            </div>
          ) : null}

          {trip.status === 'ongoing' ? (
            <Button
              title={trip.serviceType === 'delivery'
                ? (trip.parcel && trip.parcel.codAmount ? `Delivered - collect ${currency(trip.parcel.codAmount)}` : 'Delivered')
                : 'End trip & collect fare'}
              icon={trip.serviceType === 'delivery' ? '✅' : '🏁'}
              variant="accent"
              loading={busy}
              full
              style={{ marginTop: 14 }}
              onClick={() => {
                // Prepaid finishes straight away; COD asks how it was collected.
                if (!isDelivery) return completeTrip();
                if (trip.parcel && trip.parcel.codAmount > 0) return setCollectOpen(true);
                return finishDelivery('prepaid');
              }}
            />
          ) : null}

          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <button
              type="button"
              className="link-btn"
              style={{ color: colors.danger }}
              onClick={cancelTrip}
            >
              Cancel this trip
            </button>
          </div>

          {trip.deliveryOtp ? (
            <Row label="Delivery OTP (from receiver)" value={trip.deliveryOtp} />
          ) : null}
        </Reveal>
      ) : online ? (
        <div className="card sh-sm" style={{ textAlign: 'center', marginTop: 16, padding: 32 }}>
          <Pulse><span style={{ fontSize: 40 }}>📡</span></Pulse>
          <h3 className="h3" style={{ marginTop: 12 }}>Waiting for requests</h3>
          <p className="t-small" style={{ marginTop: 4 }}>
            Stay on this page. New requests ring here with a 20 second timer.
          </p>
          <div style={{ marginTop: 16 }}>
            <span className="spinner" style={{ color: colors.primary }} />
          </div>
        </div>
      ) : (
        <Empty
          emoji="🚕"
          title="You're off duty"
          text="Turn on duty above to start receiving ride and delivery requests near you."
        />
      )}

      {/* The rider's money: cash they carry is ours, earnings we owe are theirs,
          so the headline is the net and it says which way it points. */}
      {PARCEL_VEHICLES.includes(vehicleType) && cash ? (
        <Reveal className="card" style={{ marginBottom: 16 }}>
          <div className="row" style={{ alignItems: 'center' }}>
            <div className="grow">
              <div className="t-tiny">{cash.settlement >= 0 ? 'KAAMWALA OWES YOU' : 'YOU NEED TO HAND IN'}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: cash.settlement >= 0 ? 'var(--success)' : 'var(--warning)' }}>
                {currency(Math.abs(cash.settlement))}
              </div>
            </div>
            <button type="button" className="btn ghost btn-sm" onClick={loadCash}>↻</button>
          </div>
          <div className="row" style={{ gap: 16, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
            <div className="grow">
              <div className="t-tiny">CASH YOU HOLD</div>
              <strong style={{ color: 'var(--warning)' }}>{currency(cash.cashInHand)}</strong>
              <div className="t-tiny">from COD deliveries</div>
            </div>
            <div className="grow">
              <div className="t-tiny">EARNINGS PENDING</div>
              <strong style={{ color: 'var(--success)' }}>{currency(cash.earningsPending)}</strong>
              <div className="t-tiny">{cash.deliveries} deliveries this week</div>
            </div>
          </div>
          {cash.log && cash.log.length ? (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--line)', display: 'grid', gap: 6 }}>
              {cash.log.slice(0, 5).map((e, i) => {
                const positive = e.type === 'earned' || e.type === 'paid';
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="row" style={{ gap: 8 }}>
                    <span className="t-small grow">
                      {{ collected: '💵 Cash collected', settled: '✅ Cash handed in', earned: '🛵 Delivery payout', paid: '🏦 Paid to you' }[e.type]}
                      {e.orderNo ? ` · ${e.orderNo}` : ''}{e.paymentId ? ` · ${e.paymentId}` : ''}
                    </span>
                    <strong className="t-small" style={{ color: positive ? 'var(--success)' : 'var(--warning)' }}>
                      {positive ? '+' : '−'}{currency(e.amount)}
                    </strong>
                  </div>
                );
              })}
            </div>
          ) : null}
        </Reveal>
      ) : null}

      <OfferDialog offer={offer} onAccept={acceptOffer} onReject={rejectOffer} busy={busy} />

      {/* Cancelling a delivery needs a reason; after pickup it goes to admin. */}
      <Modal open={cancelOpen} onClose={() => setCancelOpen(false)} title="Cancel this delivery?">
        <p className="t-small" style={{ marginBottom: 12 }}>
          {trip && trip.status === 'ongoing'
            ? 'You already have the parcel, so admin must approve this. Keep delivering until they decide.'
            : 'The order will be offered to another rider straight away.'}
        </p>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {CANCEL_REASONS.map((r) => (
            <Chip key={r} label={r} active={cancelReason === r} onClick={() => setCancelReason(r)} />
          ))}
        </div>
        <Input placeholder="Or type what happened" value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)} />
        <div className="row" style={{ gap: 10, marginTop: 12 }}>
          <Button title="Keep delivering" variant="light" onClick={() => setCancelOpen(false)} style={{ flex: 1 }} />
          <Button
            title={trip && trip.status === 'ongoing' ? 'Ask admin' : 'Cancel delivery'}
            variant="danger"
            loading={busy}
            disabled={cancelReason.trim().length < 3}
            onClick={submitCancel}
            style={{ flex: 1.2 }}
          />
        </div>
      </Modal>

      {/* Collecting at the door: cash, or a Razorpay QR the customer scans. */}
      <Modal open={collectOpen} onClose={() => { setCollectOpen(false); setQr(null); setQrState('idle'); }}
        title={`Collect ${currency(trip && trip.parcel ? trip.parcel.codAmount : 0)}`}>
        <p className="t-small">Cash on delivery · {trip && trip.parcel && trip.parcel.description}</p>

        {qrState === 'waiting' && qr ? (
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <img src={qr.qr} alt="Scan to pay" style={{ width: 240, height: 240, borderRadius: 12 }} />
            <div className="t-small" style={{ marginTop: 8 }}>
              <span className="spinner" style={{ color: 'var(--primary)' }} /> Waiting for the customer to scan and pay…
            </div>
            <div className="t-tiny">Any UPI app works. Confirms automatically.</div>
          </div>
        ) : null}
        {qrState === 'paid' ? (
          <div style={{ background: 'var(--success-bg)', color: 'var(--success)', padding: 14, borderRadius: 12, marginTop: 14, textAlign: 'center', fontWeight: 700 }}>
            ✅ Payment received online
          </div>
        ) : null}
        {qrState === 'error' ? (
          <div className="t-small" style={{ color: 'var(--danger)', marginTop: 10 }}>
            That link could not be used. Show a new QR or take cash.
          </div>
        ) : null}

        <div style={{ display: 'grid', gap: 10, marginTop: 16 }}>
          {qrState !== 'paid' ? (
            <Button title={`Cash collected · ${currency(trip && trip.parcel ? trip.parcel.codAmount : 0)}`}
              icon="💵" variant="success" loading={busy} full onClick={() => finishDelivery('cash')} />
          ) : null}
          {qrState === 'idle' || qrState === 'error' ? (
            <Button title="Customer pays by QR (UPI)" icon="📱" variant="light" full onClick={showDoorQr} />
          ) : null}
          <Button title="Back" variant="ghost" full
            onClick={() => { setCollectOpen(false); setQr(null); setQrState('idle'); }} />
        </div>
      </Modal>
    </div>
  );
};

export default DriverRidesPage;
